import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import toast, { Toaster, ToastBar } from "react-hot-toast";
import Header from "../components/abstracts/submission/Header";
import Overview from "../components/abstracts/submission/Overview";
import Step1 from "../components/abstracts/submission/Step1";
import Step2 from "../components/abstracts/submission/Step2";
import Step3 from "../components/abstracts/submission/Step3";
import Step4 from "../components/abstracts/submission/Step4";
import UploadStep from "../components/abstracts/submission/UploadStep";
import {
  createSchemasByMode,
  validateStep,
} from "../validation/abstractSchema";

interface AbstractSubmissionProps {
  token: string;
  url: string;
  logo: string;
  iconSideToSide: string;
  iconUpAndDown: string;
  typeMappings: any[];
  currentUser: any;
  abstractTncLink: string;
  maxCorrespondingAuthors?: number;
  submissionMode: "html_input" | "file_upload";
  abstractSubmissionDeadline?: string;
}

const AbstractSubmission: React.FC<AbstractSubmissionProps> = ({
  token,
  url,
  logo,
  iconSideToSide,
  iconUpAndDown,
  typeMappings,
  currentUser,
  abstractTncLink,
  maxCorrespondingAuthors = 2,
  submissionMode,
  abstractSubmissionDeadline,
}) => {
  /* ---------------------------------------------------------------
   * Initial form data
   * --------------------------------------------------------------- */

  const getInitialFormData = () => ({
    // Step 1
    abstract_category_id: "",
    abstract_mode_id: "",
    abstract_topic_id: "",
    posterAward: {
      paParticipation: false,
      paAge: null,
      paProofOfIdentity: null,
    },

    // Step 2
    institutions: currentUser?.institution ? [currentUser.institution] : [],
    authors: [
      {
        first_name: currentUser?.firstName || "",
        last_name: currentUser?.lastName || "",
        abstract_author_institutions_attributes: currentUser?.institution
          ? [{ abstract_institution_id: currentUser.institution }]
          : [],
        corresponding_email: "",
        is_corresponding: false,
        position: "",
      },
    ],

    // Step 3 (HTML)
    title: "",
    content: "",
    abstractImages: [],
    imagePosition: "side_to_side",
    keywords: [],
    references: [],

    // File mode
    abstractFile: null,
  });

  /* ---------------------------------------------------------------
   * State
   * --------------------------------------------------------------- */

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(getInitialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ---------------------------------------------------------------
   * Schema creation (FROM PROPS — CORRECT)
   * --------------------------------------------------------------- */

  const schemas = useMemo(
    () =>
      createSchemasByMode(
        submissionMode === "file_upload" ? "file" : "html",
        maxCorrespondingAuthors,
      ),
    [submissionMode, maxCorrespondingAuthors],
  );

  /* ---------------------------------------------------------------
   * Scroll + toast cleanup on step change
   * --------------------------------------------------------------- */

  useEffect(() => {
    toast.dismiss();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  /* ---------------------------------------------------------------
   * Navigation helpers
   * --------------------------------------------------------------- */

  const handleStepChange = (step: number) => () => {
    setCurrentStep(step);
  };

  /* ---------------------------------------------------------------
   * FINAL SUBMIT HANDLER (FULL VALIDATION)
   * --------------------------------------------------------------- */

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Extract plain text from HTML title for validation
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = formData.title;
      const plainTitle = tempDiv.textContent || tempDiv.innerText || "";

      // Create validation data with plain text title
      const validationData = {
        ...formData,
        title: plainTitle,
      };

      const finalSchema = schemas.step1
        .merge(schemas.step2)
        .merge(schemas.step3);

      const validation = validateStep(validationData, finalSchema);

      if (!validation.isValid) {
        Object.values(validation.errors || {}).forEach((msg) =>
          toast.error(msg),
        );
        throw new Error("Final validation failed");
      }

      const submitData = new FormData();

      // Common fields
      submitData.append("abstract[title]", formData.title.trim());
      submitData.append(
        "abstract[abstract_category_id]",
        formData.abstract_category_id,
      );
      submitData.append(
        "abstract[abstract_mode_id]",
        formData.abstract_mode_id,
      );
      submitData.append(
        "abstract[abstract_topic_id]",
        formData.abstract_topic_id,
      );

      // Poster award
      submitData.append(
        "abstract[poster_award_attributes][pa_participation]",
        String(formData.posterAward.paParticipation),
      );

      if (formData.posterAward.paParticipation) {
        submitData.append(
          "abstract[poster_award_attributes][pa_age]",
          formData.posterAward.paAge ?? "",
        );

        if (formData.posterAward.paProofOfIdentity) {
          submitData.append(
            "abstract[poster_award_attributes][pa_proof_of_identity]",
            formData.posterAward.paProofOfIdentity,
          );
        }
      }

      // Institutions
      formData.institutions.forEach((inst: string) => {
        submitData.append("abstract[institutions][]", inst.trim());
      });

      // Authors
      formData.authors.forEach((author: any, idx: number) => {
        submitData.append(
          `abstract[authors][${idx}][first_name]`,
          author.first_name.trim(),
        );
        submitData.append(
          `abstract[authors][${idx}][last_name]`,
          author.last_name.trim(),
        );
        submitData.append(
          `abstract[authors][${idx}][corresponding_email]`,
          author.corresponding_email?.trim() || "",
        );
        submitData.append(
          `abstract[authors][${idx}][is_corresponding]`,
          String(author.is_corresponding),
        );
        submitData.append(
          `abstract[authors][${idx}][position]`,
          author.position,
        );

        author.abstract_author_institutions_attributes.forEach(
          (inst: any, instIdx: number) => {
            submitData.append(
              `abstract[authors][${idx}][abstract_author_institutions_attributes][${instIdx}][abstract_institution_id]`,
              inst.abstract_institution_id,
            );
          },
        );
      });

      // Mode-specific
      if (submissionMode === "html_input") {
        submitData.append("abstract[content]", formData.content.trim());
        submitData.append("abstract[image_position]", formData.imagePosition);

        formData.keywords.forEach((k: string) =>
          submitData.append("abstract[keywords][]", k.trim()),
        );

        formData.references.forEach((r: string) =>
          submitData.append("abstract[references][]", r.trim()),
        );

        formData.abstractImages.forEach((img: any, idx: number) => {
          submitData.append(
            `abstract[abstract_images_attributes][${idx}][image]`,
            img.file,
          );
          submitData.append(
            `abstract[abstract_images_attributes][${idx}][title]`,
            img.title || "",
          );
          submitData.append(
            `abstract[abstract_images_attributes][${idx}][position]`,
            img.position,
          );
        });
      }

      if (submissionMode === "file_upload" && formData.abstractFile) {
        submitData.append(
          "abstract[initial_submission]",
          formData.abstractFile,
        );
      }

      // Submit to backend
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
        },
        body: submitData,
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Submission successful!");
        window.location.href = result.redirect_url;
      } else {
        const error = await response.json();
        toast.error(error.error || error.message || "Submission failed!", {
          id: "submissionError",
        });
        throw new Error(error.error || error.message || "Submission failed");
      }
    } catch (error: any) {
      toast.error(error.error || error.message || "Submission failed!", {
        id: "submissionError",
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toaster
        toastOptions={{
          className: "cursor-pointer ps-3",
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div
                onClick={() => toast.dismiss(t.id)}
                className="flex items-center gap-2"
              >
                {icon}
                {message}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>

      <div
        id="submissionForm"
        className="py-10 bg-white min-h-minus-nav md:py-20"
      >
        <div className="container space-y-8">
          <Header currentStep={currentStep} submissionMode={submissionMode} />

          {currentStep === 0 && (
            <Overview
              abstractTncLink={abstractTncLink}
              logo={logo}
              abstractSubmissionDeadline={abstractSubmissionDeadline}
              onNext={handleStepChange(1)}
            />
          )}
          {currentStep === 1 && (
            <Step1
              typeMappings={typeMappings}
              formData={formData}
              setFormData={setFormData}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              submissionMode={submissionMode}
              onNext={handleStepChange(2)}
              onBack={handleStepChange(0)}
            />
          )}
          {currentStep === 2 && (
            <Step2
              formData={formData}
              setFormData={setFormData}
              currentUser={currentUser}
              maxCorrespondingAuthors={maxCorrespondingAuthors}
              submissionMode={submissionMode}
              onNext={handleStepChange(3)}
              onBack={handleStepChange(1)}
            />
          )}
          {currentStep === 3 && submissionMode === "file_upload" && (
            <UploadStep
              formData={formData}
              setFormData={setFormData}
              onBack={() => setCurrentStep(2)}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
          {currentStep === 3 && submissionMode === "html_input" && (
            <Step3
              iconSideToSide={iconSideToSide}
              iconUpAndDown={iconUpAndDown}
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(4)}
              onBack={() => setCurrentStep(2)}
            />
          )}
          {currentStep === 4 && submissionMode === "html_input" && (
            <Step4
              formData={formData}
              maxCorrespondingAuthors={maxCorrespondingAuthors}
              onBack={() => setCurrentStep(3)}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AbstractSubmission;
